import { Navigate, useLocation } from "react-router-dom";

import { PermissionAction } from "../model/permissions/types/PermissionAction";
import { PermissionType } from "../model/permissions/types/PermissionType";
import { useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";
import { UserRoles } from "../model/user/types/UserRoles";

interface AtiraRouteProps {
  component?: React.ReactNode;
  redirect?: string;
  withPermissions?: {
    type: PermissionType;
    action: PermissionAction;
  }[];
  notAllowedRoles?: UserRoles[];
  /** User must hold at least one of these roles, e.g. an org-only feature. */
  requiredRoles?: UserRoles[];
}

/** Makes sure only logged-in users can go to props.component */
export const AtiraRoute: React.FC<AtiraRouteProps> = ({
  component,
  redirect,
  withPermissions,
  notAllowedRoles,
  requiredRoles,
}) => {
  const { pathname } = useLocation();

  const loggedInUserId = useAppSelector(
    userSliceSelectors.selectLoggedInUserId,
  );
  const isLoggedIn = Boolean(loggedInUserId);
  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const permissions = loggedInUser?.permissions?.permissions;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);

  if (!isLoggedIn) {
    if (pathname.includes("/login")) {
      return component;
    }

    return (
      <Navigate to={`/login?redirect=${pathname.replace("/", "")}`} replace />
    );
  }

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  if (withPermissions?.length) {
    for (const onePermission of withPermissions) {
      if (permissions?.[onePermission.type]?.[onePermission.action] === true) {
        // The loop doesn't have to continue if the user has a permission from the available 3 tasks permissions
        // Think of it as a break statement, a hasOne() function
        return component;
      }
    }

    return <Navigate to={"/"} replace />;
  }

  if (
    notAllowedRoles?.length &&
    user?.roles?.some((role) => notAllowedRoles?.includes(role))
  ) {
    return <Navigate to={"/"} replace />;
  }

  if (
    requiredRoles?.length &&
    !user?.roles?.some((role) => requiredRoles?.includes(role))
  ) {
    return <Navigate to={"/"} replace />;
  }

  return component;
};
